import {
  Box,
  ButtonGroup,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  SkeletonText,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import AvatarPaper from "components/ui/AvatarPaper";
import ButtonBack from "components/ui/ButtonBack";
import CardForm from "components/ui/CardForm";
import DateTime from "components/ui/DateTime";
import FixedBar from "components/ui/FixedBar";
import InfoList from "components/ui/InfoList";
import SaveButton from "components/ui/SaveButton";
import SubmitButton from "components/ui/SubmitButton";
import CustomCheckBox from "features/companies/components/CustomCheckBox";
import SimpleEditor from "features/editor/components/SimpleEditor/SimpleEditor";
import { useMessages } from "hooks/useMessages";
import { useResourceList } from "hooks/useResourceList";
import {
  ObjectRequiredTestFunction,
  preventEnterKeySubmission,
} from "lib/validation";
import {
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  InputAgendaTopic,
  useSetTopicsToAgenda,
} from "../hooks/useSetTopicsToAgenda";

interface FormAgendaNotesProps {
  notes: any[];
  agendaTopics: any[];
  agenda: any;
}

interface Topic {
  id: string;
  highlighted: boolean;
}

interface FormValues {
  topics: InputAgendaTopic;
  highlightSummary: string;
  onlySave?: boolean;
}

const validationSchema = Yup.object().shape({
  topics: Yup.mixed().test("topics", "", ObjectRequiredTestFunction),
});

function FormAgendaNotes({
  notes,
  agendaTopics,
  agenda,
}: FormAgendaNotesProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const methods = useForm<FormValues>({
    mode: "all",
    resolver: yupResolver(validationSchema) as unknown as Resolver<FormValues>,
    defaultValues: {
      highlightSummary: agenda?.highlightSummary,
      topics: agendaTopics.reduce(
        (acc, item) => ({ ...acc, [item.noteTopicId]: item.highlighted }),
        {}
      ),
    },
  });

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = methods;

  const inputTopics = watch("topics");

  const handleToogleTopicHighlight = (topicId: string, isChecked: boolean) => {
    setValue("topics", { ...inputTopics, [topicId]: isChecked });
  };

  const handleToogleTopic = (topic: Topic, isChecked: boolean) => {
    const newItems = isChecked
      ? { ...inputTopics, [topic.id]: topic.highlighted }
      : Object.entries(inputTopics).reduce((acc, [k, v]) => {
          return k !== topic.id ? { ...acc, [k]: v } : acc;
        }, {});
    setValue("topics", newItems, { shouldValidate: true });
  };

  const { onSuccess } = useMessages({
    success: "Fique tranquilo, seu trabalho foi salvo",
  });

  const { onSetTopicsToAgenda, isPending, variables } = useSetTopicsToAgenda({
    agendaId: agenda.id,
    onSuccess: (onlySave) => {
      if (onlySave) onSuccess();
      else
        navigate(
          `/disparos-de-agenda/editar/${agenda.id}/conferir-dados${location.search}`
        );
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (data) =>
    onSetTopicsToAgenda(data);

  const onSave: SubmitHandler<FormValues> = (data) =>
    onSubmit({ ...data, onlySave: true });

  return (
    <FormProvider {...methods}>
      <form
        onKeyPress={preventEnterKeySubmission}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Heading as="h1" size="h1">
          Notas
          <span>
            Selecione quais tópicos de cada nota fazem parte do disparo
          </span>
        </Heading>

        <VStack mt="20px" spacing="20px" w="full">
          {notes?.map((note) => (
            <Box
              key={note.id}
              w="full"
              borderRadius="10px"
              border="1px solid rgba(0, 0, 0, 0.10)"
              bgColor="white"
              padding="30px"
            >
              <Wrap direction="column">
                <WrapItem key={note.id} flexDir="column">
                  <Box py="15px" px="20px" w="full">
                    <HStack spacing="12px">
                      <AvatarPaper />
                      <Heading as="h3" size="h3">
                        {note.title}
                      </Heading>
                    </HStack>

                    <Box w="full" pl="20px">
                      <InfoList>
                        <Box>
                          <Text as="span">
                            <small>Data</small>
                            <DateTime date={note.date} hideTime />
                          </Text>
                        </Box>

                        <Box>
                          <Text as="span">
                            <small>Ocultar data</small>
                            {note.hideDate ? "Sim" : "Não"}
                          </Text>
                        </Box>

                        {!!note.startsAt && (
                          <Box>
                            <Text as="span">
                              <small>Horário</small>
                              {note.startsAt}
                              {!!note.endsAt && ` - ${note.endsAt}`}
                            </Text>
                          </Box>
                        )}
                        <Box>
                          <Text as="span">
                            <small>Categoria</small>
                            {`${note.category.name} - ${note.subcategory.name}`}
                          </Text>
                        </Box>
                        <Box>
                          <Text as="span">
                            <small>Criado por</small>
                            {note.createdBy.name}
                          </Text>
                        </Box>
                      </InfoList>
                    </Box>
                  </Box>

                  <Divider mt="5px" mb="15px" />

                  <Table mt="10px" mb="30px">
                    <Thead>
                      <Tr>
                        <Th w="auto">Tópico</Th>
                        <Th textAlign="center">Destaque</Th>
                      </Tr>
                    </Thead>
                    <Tbody borderColor="gray.100" borderWidth="1px">
                      {note.topics.map((topic) => {
                        const inputTopic = inputTopics[topic.id];
                        const isChecked = inputTopic !== undefined;
                        return (
                          <Tr key={topic.id}>
                            <Td>
                              <HStack spacing="10px">
                                <Box w="20px">
                                  <CustomCheckBox
                                    hideIcon
                                    name="topics"
                                    value={topic.id}
                                    isChecked={isChecked}
                                    label={""}
                                    description={``}
                                    onChange={(e) =>
                                      handleToogleTopic(topic, e.target.checked)
                                    }
                                  />
                                </Box>
                                <Flex
                                  minH="38px"
                                  w="full"
                                  justifyContent="center"
                                  flexDir="column"
                                  color="#525866"
                                  fontSize="12px"
                                  lineHeight="133.333%"
                                  dangerouslySetInnerHTML={{
                                    __html: topic.text,
                                  }}
                                />
                              </HStack>
                            </Td>
                            <Td w="130px" textAlign="center">
                              {isChecked && (
                                <FormControl>
                                  <Switch
                                    isChecked={!!inputTopic}
                                    onChange={(e) =>
                                      handleToogleTopicHighlight(
                                        topic.id,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </FormControl>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </WrapItem>
              </Wrap>
            </Box>
          ))}
        </VStack>

        <Divider mt="48px" mb="60px" />

        <Heading as="h1" size="h1">
          Resumo dos destaques
          <span>Digite o resumo dos destaques</span>
        </Heading>

        <CardForm>
          <FormControl>
            <FormLabel>Resumo</FormLabel>
            <SimpleEditor
              name="highlightSummary"
              control={control}
              maxLength={1500}
            />
          </FormControl>
        </CardForm>

        <FixedBar>
          <ButtonBack
            as={Link}
            to={{
              pathname: `/disparos-de-agenda/editar/${agenda.id}`,
              search: location.search,
            }}
          />

          <ButtonGroup>
            <SaveButton
              isDisabled={!isValid}
              isLoading={variables?.onlySave && isPending}
              onClick={handleSubmit(onSave)}
            >
              Salvar
            </SaveButton>

            <SubmitButton
              isDisabled={!isValid}
              isLoading={!variables?.onlySave && isPending}
            >
              Salvar e continuar
            </SubmitButton>
          </ButtonGroup>
        </FixedBar>
      </form>
    </FormProvider>
  );
}

export default function FormAgendaNotesContainer({ agenda }: any) {
  const { data: { notes, topics } = {}, isLoading } = useResourceList({
    resourceName: "agendaNotes",
    path: `agendas/${agenda.id}/notes`,
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <Container variant="internal" flexDirection="column" mb="150px">
      <SkeletonText isLoaded={!isLoading}>
        {!isLoading && (
          <FormAgendaNotes
            agenda={agenda}
            notes={notes}
            agendaTopics={topics}
          />
        )}
      </SkeletonText>
    </Container>
  );
}
